import { useCallback, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEntityStore } from '@ContractBuilder/store';
import { createEndorsement } from '@mutations/createEndorsement';
import Nav from '@src/routes';

import type { EndorsementFormState } from '../types';

interface UseEndorsementMutationArgs {
  submissionId?: string;
  onSuccess: (id?: string) => void;
}

export function useEndorsementMutation({ submissionId, onSuccess }: UseEndorsementMutationArgs) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateEndorsement = useEntityStore(({ updateResourceData }) => updateResourceData);

  const openEndorsement = useCallback(
    (id: string) => {
      if (id && submissionId) {
        const route = generatePath(Nav.EndorsementView, {
          id: submissionId,
          endorsement_id: id,
        });

        const win = window.open(route, '_self');
        win?.focus();
      }
    },
    [submissionId],
  );

  const onCreate = useCallback(
    async (data: EndorsementFormState) => {
      if (!submissionId) {
        setError('Submission ID is required');
        toast.error('Failed to create endorsement: No submission ID');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const create = createEndorsement(submissionId);
        const payload = { ...data, parentSubmissionId: undefined };
        const { id } = await create(payload);
        openEndorsement(id);
        onSuccess(id);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create endorsement';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [submissionId, openEndorsement, onSuccess],
  );

  const onUpdate = useCallback(
    async (data: EndorsementFormState) => {
      setIsLoading(true);
      setError(null);

      try {
        // The summary template is read-only in edit mode (EndorsementModalController) and the
        // strict updateEndorsementPayloadSchema rejects unknown keys, so sending the id 400s
        // the whole save. Omit it rather than widening the schema — that would stamp the
        // edit-form's `?? 1` default onto endorsements created before summary templates existed.
        const { endorsement_summary_template_id: _omitted, ...payload } = data;
        await updateEndorsement(payload);
        toast.success('Data saved successfully', { autoClose: 5000 });
        onSuccess();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update endorsement';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [updateEndorsement, onSuccess],
  );

  return {
    error,
    isLoading,
    onCreate,
    onUpdate,
  };
}
